import type { TransientStayFormInput } from './transient-stay-form-schema'
import { transientStayFormDefaults } from './transient-stay-form-schema'
import type {
  TransientStayTableRow,
  TransientStatus,
  BookingType,
  RateType,
} from '@/types/transient-stays'

/**
 * CamelCase shape for transient stay data passed from hangar-view enriched data.
 * Matches the subset of TransientStayInfo fields that are editable inline.
 */
export interface TransientStayFormInitialData {
  status: TransientStatus
  checkIn: string
  expectedCheckOut: string
  rateType: RateType | null
  rateAmount: number | null
  bookingType: BookingType
  notes?: string | null
}

/**
 * Format ISO date string to datetime-local input format.
 */
export function formatDateTimeLocal(isoString: string): string {
  const date = new Date(isoString)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${year}-${month}-${day}T${hours}:${minutes}`
}

/**
 * Convert a full DB row (snake_case TransientStayTableRow) to form defaults.
 * Used by TransientStayMutateDrawer when loading existing stay for editing.
 */
export function transientStayRowToFormDefaults(
  stay: TransientStayTableRow
): TransientStayFormInput {
  return {
    location_id: stay.location_id,
    client_id: stay.client_id,
    aircraft_id: stay.aircraft_id,
    unit_id: stay.unit_id || '',
    check_in: formatDateTimeLocal(stay.check_in),
    expected_check_out: formatDateTimeLocal(stay.expected_check_out),
    actual_check_out: stay.actual_check_out
      ? formatDateTimeLocal(stay.actual_check_out)
      : null,
    booking_type: stay.booking_type,
    status: stay.status,
    source: stay.source,
    rate_type: stay.rate_type,
    rate_amount: stay.rate_amount,
    discount_amount: stay.discount_amount,
    discount_reason: stay.discount_reason,
    tax_amount: stay.tax_amount,
    notes: stay.notes,
    special_requests: stay.special_requests,
  }
}

/**
 * Convert camelCase enriched data (from hangar view card) to form defaults.
 * Only populates the inline-editable subset; others stay at defaults.
 */
export function transientStayInitialDataToFormDefaults(
  data: TransientStayFormInitialData
): TransientStayFormInput {
  return {
    ...transientStayFormDefaults,
    status: data.status,
    check_in: data.checkIn,
    expected_check_out: data.expectedCheckOut,
    rate_type: data.rateType,
    rate_amount: data.rateAmount,
    booking_type: data.bookingType,
    notes: data.notes ?? null,
  }
}

/**
 * Dispatcher: normalize transient stay data from either source.
 */
export function normalizeTransientStayData(
  stay?: TransientStayTableRow | null,
  initialData?: TransientStayFormInitialData | null
): TransientStayFormInput {
  if (stay) return transientStayRowToFormDefaults(stay)
  if (initialData) return transientStayInitialDataToFormDefaults(initialData)
  return transientStayFormDefaults
}

/**
 * Map camelCase prefocus field names to snake_case form field names.
 */
export const TRANSIENT_PREFOCUS_FIELD_MAP: Record<string, string> = {
  checkIn: 'check_in',
  expectedCheckOut: 'expected_check_out',
  status: 'status',
  rateType: 'rate_type',
  rateAmount: 'rate_amount',
  bookingType: 'booking_type',
  notes: 'notes',
}
